import {
    ActivityProduct,
    AnyBookingProduct,
    BaggageInsuranceProduct,
    CabinClass,
    CancellationInsuranceProduct,
    Coupon,
    CouponType,
    CustomBaggageProduct,
    CustomerServiceMessage,
    CustomSeatingProduct,
    FerryProduct,
    FlightBaggageType,
    FlightLeg,
    FlightProduct,
    FlightSegment,
    FlightTicketVerification,
    FlightType,
    HotelProduct,
    ImportantInformation,
    InformationType,
    MyBooking,
    MyBookingStatus,
    PaymentMethodType,
    ProductType,
    ServicePackageProduct,
    TransferProduct,
    TravelInsuranceProduct,
    UsedPaymentMethod,
} from './myBooking.interfaces';
import { range, sample, shuffle } from 'lodash';
import { generateRandomNumber, generateUUID } from '@shared/utils/uuid';
import { getStoredFakeData } from '@/components/shared/DevPanel/DevPanel.helpers';
import { getTravelTime } from '@ui/components/Journey/Journey.helpers';
import {
    convertToDate,
    getDateAddingDuration,
    getDateSubtractingDuration,
    getFormattedDate,
    getShortDate,
    today,
} from '@shared/helpers/datetime/datetime';
import {
    getFakeBookingId,
    getFakeBookingNumber,
    getFakePassword,
} from '@/helpers/queryStrings';
import { TransferJourneyType } from '@/helpers/transfer/transfer.interfaces';
import { DateFormat } from '@shared/interfaces/dates';
import { FakeDataRyanairOption } from '@/components/shared/DevPanel/DevPanel.interfaces';
import { useNowUTC } from '@/helpers/useNow';
import { Market } from '@shared/config/market/market.models';
import { getCurrentMarketSpecs } from '@shared/config/market/market.helpers';

const stored = getStoredFakeData();
const market: Market = getCurrentMarketSpecs();
const { currency } = market.moneyFormat;

const getRandomFromArray = (array: any[], defaultValue = '') => {
    return sample(array) || defaultValue;
};

const getStatus = (): MyBookingStatus => {
    return stored.booking?.status ?? MyBookingStatus.Completed;
};

const getAmountAlreadyPaid = () => {
    if (stored.booking?.hasAmountAlreadyPaid) {
        return {
            amount: { amount: 2500, currency },
        };
    }

    return {
        amount: { amount: 0, currency },
    };
};

const getFinalPayment = () => {
    if (stored.booking?.hasFinalPayment) {
        return {
            amount: { amount: 17945, currency },
            dueTime: getFormattedDate(
                convertToDate(
                    getDateAddingDuration(new Date(), {
                        days: stored.booking?.hasCloseFinalPayment ? 2 : 31,
                    })
                ),
                DateFormat.ISO
            ),
        };
    }

    return null;
};

const getDownPayment = () => {
    if (stored.booking?.hasDownPayment) {
        return {
            amount: { amount: 1499, currency },
            dueTime: getFormattedDate(
                convertToDate(getDateAddingDuration(new Date(), { hours: 2 })),
                DateFormat.ISO
            ),
        };
    }

    return null;
};

const getCreated = (): string => {
    const duration = { hours: stored.booking?.createdToday ? 10 : 100 };

    return getDateSubtractingDuration(new Date(), duration).toISOString();
};

function getFakeRyanairOptionValue<T>(
    option: FakeDataRyanairOption,
    index: number,
    values: [T, T]
): T | null {
    switch (option) {
        case FakeDataRyanairOption.All:
            return values[0];
        case FakeDataRyanairOption.Some:
            return index % 2 === 0 ? values[0] : values[1];
        default:
            return null;
    }
}

const getFlightTicketType = (): FlightType => {
    if (stored.booking?.ryanair?.enabled) {
        return FlightType.Ryanair;
    }

    return sample([FlightType.LCC, FlightType.Other, FlightType.TravelFusion]);
};

function getFlightTicketVerification(
    ticketType: FlightType,
    index: number
): FlightTicketVerification | null {
    if (ticketType !== FlightType.Ryanair) return null;

    const { now } = useNowUTC();
    const nowDate = new Date(now.value);

    const url = getFakeRyanairOptionValue(stored.booking.ryanair.hasUrl, index, [
        'https://www.ryanair.com',
        null,
    ]);

    const confirmed =
        getFakeRyanairOptionValue(stored.booking.ryanair.isConfirmed, index + 1, [
            true,
            false,
        ]) ?? false;

    const validUntil = url
        ? getFakeRyanairOptionValue<string | null>(
              stored.booking.ryanair.isUrlValid,
              index + 2,
              [
                  getFormattedDate(
                      getDateAddingDuration(nowDate, { minutes: 1 }),
                      DateFormat.ISO
                  ),
                  getFormattedDate(
                      getDateSubtractingDuration(nowDate, { minutes: 5 }),
                      DateFormat.ISO
                  ),
              ]
          )
        : null;

    return {
        url: url as string | null,
        confirmed,
        validUntil,
    };
}

const generateFlight = (index: number): FlightProduct => {
    const getCarrierLogo = (carrier?: string): string | null => {
        return carrier
            ? `https://www.sembo.se/d/Images/flight/airlines/${carrier}.gif`
            : null;
    };

    const daysOffset = shuffle(range(10, 30));

    const ticketAirlineNumber = sample([null, 'AR12345']);
    const ticketType = getFlightTicketType();
    const ticketNumber =
        ticketAirlineNumber && ticketType !== FlightType.LCC ? `123456789-123${index}` : '';

    const companies = stored.booking?.ryanair?.enabled
        ? [{ code: 'FR', name: 'Ryanair' }]
        : [
              { code: 'BT', name: 'Air Baltic' },
              { code: 'AC', name: 'Air Canada' },
              { code: 'AF', name: 'Air France' },
              { code: 'AA', name: 'American Airlines' },
              { code: 'RC', name: 'Atlantic Airways' },
              { code: 'OS', name: 'Austrian Airlines' },
              { code: 'BA', name: 'British Airways' },
              { code: 'SN', name: 'Brussels Airlines' },
              { code: 'CX', name: 'Cathay Pacific' },
              { code: 'EK', name: 'Emirates' },
              { code: 'ET', name: 'Ethiopian airlines' },
              { code: 'AY', name: 'Finnair' },
              { code: 'IB', name: 'Iberia' },
              { code: 'KL', name: 'KLM' },
              { code: 'LO', name: 'LOT' },
              { code: 'LH', name: 'Lufthansa' },
              { code: 'DY', name: 'Norwegian Air Shuttle' },
              { code: 'QF', name: 'Qantas' },
              { code: 'QR', name: 'Qatar Airways' },
              { code: 'SK', name: 'SAS' },
              { code: 'LX', name: 'Swiss' },
              { code: 'TG', name: 'Thai Airways' },
              { code: 'TK', name: 'Turkish Airlines' },
          ];

    const locations = [
        {
            code: 'ARN',
            name: 'Arlanda',
            country: 'Sverige',
            location: 'Stockholm',
        },
        {
            code: 'CPH',
            name: 'Kastrup',
            country: 'Danmark',
            location: 'Köpenhamn',
        },
        {
            code: 'LGW',
            name: 'Gatwick',
            country: 'Storbritannien',
            location: 'London',
        },
        {
            code: 'BKK',
            name: 'Suvarnabhumi (Bangkok Airport)',
            country: 'Thailand',
            location: 'Bangkok',
        },
        {
            code: 'CDG',
            name: 'Charles de Gaulle',
            country: 'Frankrike',
            location: 'Paris',
        },
        {
            code: 'FCO',
            name: 'Rome-Fiumicinos',
            country: 'Italien',
            location: 'Rome',
        },
        {
            code: 'PMI',
            name: 'Son Sant Joan',
            country: 'Spanien',
            location: 'Palma de Mallorca',
        },
        {
            code: 'BCN',
            name: 'El Prat',
            country: 'Spanien',
            location: 'Barcelona',
        },
    ];

    const getLeg = (inBound = true): FlightLeg => {
        const tripStarting = daysOffset.pop() ?? 1;

        const daysToAdd = inBound ? tripStarting : tripStarting + 7;

        const company = sample(companies);

        return {
            segments: getSegments(daysToAdd),
            platingCarrier: company?.code ?? '',
            platingCarrierName: company?.name ?? '',
            platingCarrierLogo: getCarrierLogo(company?.code),
            travelTime: '4h',
        };
    };

    const getSegments = (daysToAdd: number) => {
        let previousIndex = Math.floor(Math.random() * locations.length);

        const startDate = getDateAddingDuration(today(), {
            days: daysToAdd,
            hours: sample(range(8, 19)),
            minutes: 5,
        });

        const segmentsCount = sample([1, 2, 3, 4]);
        const dates = [...new Array(segmentsCount * 2)].map((_, index) =>
            getDateAddingDuration(startDate, { hours: index + 3 })
        );

        const getAirPortName = (location: any) =>
            `${location.location}, ${location.name} (${location.code}), ${location.country}`;

        return [...new Array(segmentsCount)].map((): FlightSegment => {
            const departureDate = dates.shift() ?? new Date();
            const arrivalDate = dates.shift() ?? new Date();

            const nextPreviousIndex = previousIndex + 1;
            const nextIndex = nextPreviousIndex < locations.length ? nextPreviousIndex : 0;
            const departure = locations[previousIndex];
            const arrival = locations[nextIndex];

            const company = sample(companies);
            const operatingCompany = sample(companies);

            previousIndex = nextIndex;

            return {
                departureDate: getFormattedDate(departureDate, DateFormat.ISO),
                departureAirportName: getAirPortName(departure),
                departureAirportCode: departure.code,
                departureCountryName: departure.country,
                departureCityName: departure.location,
                arrivalAirportName: getAirPortName(arrival),
                arrivalAirportCode: arrival.code,
                arrivalCountryName: arrival.country,
                arrivalCityName: arrival.location,
                arrivalDate: getFormattedDate(arrivalDate, DateFormat.ISO),
                flightNumber: `${company?.code}${Math.floor(Math.random() * 9999)}`,
                flightTime: getTravelTime(departureDate, arrivalDate),
                cabinClassType: CabinClass.Economy,
                stopOverTime: '1h 25m',
                marketingCarrier: company?.code ?? '',
                marketingCarrierName: company?.name ?? '',
                marketingCarrierLogo: getCarrierLogo(company?.code),
                operatingCarrier: operatingCompany?.code ?? '',
                operatingCarrierName: operatingCompany?.name ?? '',
                operatingCarrierLogo: getCarrierLogo(operatingCompany?.code),
                hasTechnicalStop: segmentsCount === 1,
            };
        });
    };

    const status = getStatus();
    const isCancelled =
        status === MyBookingStatus.Aborted || status === MyBookingStatus.Cancelled;

    const refund = isCancelled ? { pending: index % 2 === 0, date: null } : null;

    const verification = getFlightTicketVerification(ticketType, index);

    return {
        type: ProductType.Flight,
        ticket: {
            airlineNumber: ticketAirlineNumber,
            type: ticketType,
            boardingPassUrl: null,
            pnr: generateUUID(),
            verification,
        },
        refund,
        pax: [
            {
                ticketNumber,
                title: 2,
                firstName: 'JOHN',
                lastName: 'DOHN',
                dateOfBirth: '06/25/1978 00:00:00',
                nationality: null,
                passportNumber: null,
                passportExpirationDate: null,
                baggages: [
                    {
                        description: null,
                        type: FlightBaggageType.CabinBag,
                        pieces: 1,
                        weight: null,
                    },
                ],
                seats: [
                    {
                        description: 'Seat:12F-MHDIST',
                        row: 12,
                        column: 'F',
                        departureAirportCode: 'MHD',
                        arrivalAirportCode: 'IST',
                    },
                    {
                        description: 'Seat:7D-CPHIST',
                        row: 7,
                        column: 'D',
                        departureAirportCode: 'CPH',
                        arrivalAirportCode: 'IST',
                    },
                ],
            },
            {
                ticketNumber,
                title: 1,
                firstName: 'NILS HUGO',
                lastName: 'SVENSSON',
                dateOfBirth: '07/05/1972 00:00:00',
                nationality: null,
                passportNumber: null,
                passportExpirationDate: null,
                baggages: [
                    {
                        description: 'OBO',
                        type: FlightBaggageType.CabinBag,
                        pieces: 1,
                        weight: null,
                    },
                    {
                        description: 'IBO',
                        type: FlightBaggageType.CheckedBag,
                        pieces: 1,
                        weight: 23,
                    },
                ],
                seats: [],
            },
            {
                ticketNumber,
                title: 1,
                firstName: 'ADRIAN',
                lastName: 'WOOD',
                dateOfBirth: '06/21/2009 00:00:00',
                nationality: null,
                passportNumber: null,
                passportExpirationDate: null,
                baggages: [
                    {
                        description: null,
                        type: FlightBaggageType.CheckedBag,
                        pieces: 2,
                        weight: 23,
                    },
                ],
                seats: [
                    {
                        description: 'Seat:12D-MHDIST',
                        row: 12,
                        column: 'D',
                        departureAirportCode: 'MHD',
                        arrivalAirportCode: 'IST',
                    },
                    {
                        description: 'Seat:6C-ISTMHD',
                        row: 6,
                        column: 'C',
                        departureAirportCode: 'IST',
                        arrivalAirportCode: 'MHD',
                    },
                ],
            },
        ],
        legs: index % 2 ? [getLeg(), getLeg(false)] : [getLeg()],
    };
};

const generateFerry = (index: number): FerryProduct => {
    const logo =
        index % 2 === 0
            ? 'https://cdn.sanity.io/files/inntox6v/production/9043ce3577c212b0582fa57d652939b2292c6c0e.png/Stena-Line.svg'
            : null;

    const destinations = ['Gothenburg', 'Frederikshavn'];
    const [departure, arrival] = index % 2 === 0 ? destinations : destinations.reverse();

    return {
        productId: generateRandomNumber(),
        type: ProductType.Ferry,
        pax: [
            {
                title: 1,
                firstName: 'Kalle',
                lastName: 'Nilsson',
                specialNeedsOption: 0,
                dateOfBirth: '1997-01-01',
                nationality: null,
            },
            {
                title: 3,
                firstName: 'Klara',
                lastName: 'Kallesson',
                specialNeedsOption: null,
                dateOfBirth: '1994-01-01',
                nationality: null,
            },
        ],
        legs: [
            {
                segments: [
                    {
                        legName: `${departure} - ${arrival} - ECONOMY`,
                        departurePortName: departure,
                        arrivalPortName: arrival,
                        departurePortCode: null,
                        arrivalPortCode: null,
                        ferryName: 'Stena Line',
                        operatingCarrierLogoUrl: logo,
                        vehicle: 'Motorbike',
                        vehicleRegNo: '1234-ABC',
                        departureDate: getFormattedDate(
                            getDateAddingDuration(new Date(), {
                                days: index * 3,
                                hours: 2,
                            }),
                            DateFormat.ISO
                        ),
                        arrivalDate: getFormattedDate(
                            getDateAddingDuration(new Date(), {
                                days: index * 3,
                                hours: 4,
                                minutes: 8,
                            }),
                            DateFormat.ISO
                        ),
                        accommodations: [],
                        meals: [],
                        misc: [],
                        pets: [
                            {
                                title: 'Husdjur ombord - Husdjur ombord - Hund ombord, på anvisad plats (inomhus)',
                                quantity: 1,
                            },
                        ],
                        lounges: [
                            {
                                title: 'Pluslounge - Plus Lounge, avskild lounge inkl snacks Fr 18 år',
                                quantity: 1,
                            },
                        ],
                    },
                ],
            },
        ],
    };
};

const generateActivity = (index: number): ActivityProduct => {
    const date = getDateAddingDuration(new Date(), {
        days: 10,
        hours: 4,
    });
    const release = getDateSubtractingDuration(date, {
        days: 3,
    });

    return {
        type: ProductType.Activity,
        placeCode: '3256047',
        placeName: 'Sembo Activity test',
        imageUrl:
            'https://media-cdn.tripadvisor.com/media/attractions-splice-spp-720x480/10/37/50/b9.jpg',
        date: getFormattedDate(date, DateFormat.ISO),
        voucherAvailability:
            index % 2 === 0 ? null : getFormattedDate(release, DateFormat.ISO),
        description: 'Sembo Activity description',
        categoryDescription: `Join us for an engaging and interactive experience designed to bring people together while fostering creativity, learning, and fun. This carefully planned activity combines hands-on participation with collaborative elements, ensuring that participants of all skill levels can contribute meaningfully and enjoy themselves.`,
        tickets: [
            {
                productId: 27084062555,
                bookingReference: null,
                adults: 1,
                children: 1,
            },
        ],
    };
};

const generateGolfActivity = (): ActivityProduct => {
    return {
        type: ProductType.Activity,
        placeCode: 'ONTEE-EUR',
        placeName: 'Arabella Golf: Golf Son Vida - Tee 1',
        imageUrl: 'https://letsgolfstorage.blob.core.windows.net/golfclubimages/1696_1.jpg',
        date: getFormattedDate(
            getDateAddingDuration(new Date(), {
                days: 10,
                hours: 4,
            }),
            DateFormat.ISO
        ),
        description: 'Golf Son Vida',
        categoryDescription: `Join us for an engaging and interactive experience designed to bring people together while fostering creativity, learning, and fun. This carefully planned activity combines hands-on participation with collaborative elements, ensuring that participants of all skill levels can contribute meaningfully and enjoy themselves.`,
        tickets: [
            {
                productId: generateRandomNumber(),
                bookingReference: null,
                adults: 1,
                children: 0,
            },
            {
                productId: generateRandomNumber(),
                bookingReference: null,
                adults: 0,
                children: 1,
            },
        ],
    };
};

const generateHotel = (index: number): HotelProduct => {
    const images: string[] = [
        'https://i.travelapi.com/lodging/17000000/16720000/16714700/16714624/30543052_z.jpg',
    ];

    const hotelInfo = getHotelInformation(index);

    const generateRooms = () => {
        const notes = [
            'Phone number: 928771125; Mandatory Deposit,  Fee Amount: 150,  Currency: EUR.',
            "WARNINGS: Conditions:. 1. If you have any active booking or cancelled and rebook with new price hotel will not confirm the last booking (TR: Ayn otelde aktif yada iptal edilip yeni fiyatla tekrar yapılan rezervasyon olması durumunda konfirme edilmeyecektir). 2. Late Check-in. Some hotels do not have 24 hour reception service. If you are going to arrive to property after 08:00 pm or later please contact to hotel and inform them your arrival time. In case you'll arrive to hotel after midnight and didn't inform hotel could result cancelling your room by hotel and no-show.. 3. Requests. Bedding types for Double/Twin rooms, Specific room types cannot be guaranteed and are subject to availability at the time of check in. Extra bed depends on bedding type or condition of the hotel , there will be an additional charge for extra beds.. 4. Hotel surcharges. Resort fees, city taxes or any other surcharges due to the country regulations must be paid to the hotel directly. Our agency is not responsible for any extra hotel fees.. 5. Early check in & Late Check out. Hotels in general check the guests in to property after 02:00 pm and Check Out could be latest 11:00 am. Hotel could charge extra for the special requests..",
            null,
        ];

        const getNote = () => {
            return notes[sample([0, 1, 2])];
        };

        const secondRoomId = sample([270840633, 270840653]);

        const rooms = [
            {
                productId: 270840622,
                roomDescription: 'Standard room with 1 double bed and sofa, Frukost',
                adults: 2,
                children: 1,
                accommodationNotes: getNote(),
            },
            {
                productId: secondRoomId,
                roomDescription: 'Deluxe room with 1 double bed and sofa, Frukost',
                adults: 1,
                children: 1,
                accommodationNotes: getNote(),
            },
        ];

        return rooms.slice(0, sample([1, 2]));
    };

    return {
        ...hotelInfo,
        type: ProductType.Hotel,
        url: 'https://sembo.se',
        imageUrl: getRandomFromArray(images),
        fromDate: getFormattedDate(
            getDateAddingDuration(new Date(), {
                days: 4 + index,
                hours: 4 + index,
            }),
            DateFormat.ISO
        ),
        toDate: getFormattedDate(
            getDateAddingDuration(new Date(), {
                days: 10 + index,
                hours: 4 + index,
            }),
            DateFormat.ISO
        ),
        checkInHours: '15:00',
        checkOutHours: '11:00',
        rooms: generateRooms(),
    };
};

const getHotelInformation = (index: number) => {
    const array = [
        {
            address: {
                streetAddress: 'C Roure s/n ',
                city: 'Puerto Pollensa',
                country: 'Spain',
                phone: '+34 971 718571',
                zipCode: '',
            },
            hotelCode: '19886',
            hotelName: 'Sivella Apartments',
        },
        {
            address: {
                streetAddress: 'Cervantes  22 ',
                city: 'Ses Salines  ',
                country: 'ES',
                phone: '0-678163635 | ',
                zipCode: '07638',
            },
            hotelCode: '4436763',
            hotelName: 'Lavendel Apartments',
        },
        {
            address: {
                streetAddress:
                    'Kadınlar Denizi, Yüksel Yalova Cd. No:36, 09400 Kadinlardenizi Kusadasi/Kuşadası/Kuşadası/Aydın',
                city: 'Kusadasi',
                country: 'TR',
                phone: '0-96781693635 | ',
                zipCode: '09400',
            },
            hotelCode: 'TR-H31432',
            hotelName: 'Tuntas Family Suites Kusadasi',
        },
        {
            address: {
                streetAddress: 'Avenida del Rey Jaume I 76 ',
                city: 'Calvia  Balearic Islands',
                country: 'ES',
                phone: '34-871-225 | 34-871-225',
                zipCode: '7000',
            },
            hotelCode: '21785',
            hotelName: 'Hotel Playas del Rey',
        },
        {
            address: {
                streetAddress: 'Via Naviglio',
                city: 'SAN TEODORO',
                country: 'IT',
                phone: null,
                zipCode: '08020',
            },
            hotelCode: 'I-3326',
            hotelName: 'Terra Di Mare Resort & Spa',
        },
        {
            address: {
                streetAddress: 'Cannaregio 1804 ',
                city: 'Venice  VE',
                country: 'IT',
                phone: '39-348-1813782 | 39-348-1813782',
                zipCode: '30121',
            },
            hotelCode: 'I-H220010',
            hotelName: 'Al Portico Guest House Venezia',
        },
    ];

    return array[index] ?? array[0];
};

const generateTransfer = (index: number): TransferProduct => {
    const date = getShortDate(getDateAddingDuration(new Date(), { days: 10 }));
    const even = index % 2 === 0;

    return {
        type: ProductType.Transfer,
        productId: generateRandomNumber(),
        from: {
            dateTime: `${date}T00:00:00`,
            address: null,
            iataInfo: {
                code: 'CDG',
                label: 'Paris, Charles de Gaulle (CDG), France',
                labelShort: 'Paris, Charles de Gaulle (CDG)',
                city: 'Paris',
                country: 'France',
            },
        },
        to: {
            dateTime: `${date}T00:00:00`,
            address: "Disney's Hotel Santa Fe",
            iataInfo: {
                code: null,
                label: null,
                labelShort: null,
                city: null,
                country: null,
            },
        },
        flightNo: 'AF 1553',
        pickUpTime: '08:55',
        journeyType: TransferJourneyType.FlightHotel,
        duration: 90,
        conditions: even
            ? 'Please check your booking details above  If you need to amend or cancel your booking you must contact the travel agent to make any changes or cancellations to your booking     Please be aware that we require 48hours notice for any amendments needed in regards to your pick up time   location     In the event of any emergency amendments  we will do our best to accommodate your needs  however we are unable to guarantee that our partner will be able to accommodate these changes at short notice      What to do when arriving at BCN   On arrival please follow the instructions below     Final confirmation of your meeting point instructions will be sent via WhatsApp or SMS 24 hours before your service is scheduled If you d like to speak with a member of our team  please call us on 0044 1483 802408       Once you have located your transfer representative  simply present this e ticket to them and you will be escorted to your vehicle       Your shuttle will depart no more than 60 minutes after meeting your transfer representative       What to do if your arrival time is likely to be delayed  or you are unable to locate your representative      In the event of a delay due to a late arrival  or if you are unable to locate your representative please make contact with your transfer provider immediately on one of the numbers listed below     Contact Numbers   Resort office  0044 1483 802408  Emergency  0044 1483 802408  '
            : 'Please check your booking details above  If you need to amend or cancel your booking you must contact the travel agent to make any changes or cancellations to your booking     Please be aware that we require 48hours notice for any amendments needed in regards to your pick up time   location     In the event of any emergency amendments  we will do our best to accommodate your needs  however we are unable to guarantee that our partner will be able to accommodate these changes at short notice      What to do when leaving 54 Salford Road  london  SW24BG  Your driver will pick you up from 54 Salford Road  london  SW24BG on 12 07 2025 at 11 44  Please be ready 15 minutes prior to this       If you have opted in to receiving SMS text messages from us a reminder of your pickup time will automatically be sent to your phone        In the unlikely event of any problems arising on the day  please contact one of the emergency numbers listed below       On the day of your departure     Once you know your scheduled pickup time  please be at the meeting point at least 15 minutes before vehicles scheduled arrival time       Return services can from time to time run slightly late due to traffic or other passengers running late  Your vehicle should arrive no later than fifteen minutes after your scheduled pickup time  Should your vehicle not arrive  please make contact with your transfer provider immediately on one of the numbers listed below who will advise you on finding an alternative method of transport    Contact Numbers   Resort office   44 1483 802408  Emergency  0018008144204',
        luggage: 2,
        partner: {
            name: 'Sixt (EUR)',
            image: 'https://img.mydriver.com/image/upload/t_web/v1549011723/logos/ride/Sixt_ride_2d_4c_pos.png',
        },
        passengers: {
            adults: 2,
            children: 1,
            infants: 1,
        },
        shieldText: 'Mr. Potato',
        vehicle: {
            image: 'https://www.hoppa.com/images/vehicle_images/ret2_private.jpg',
            model: 'Hyundai Sonata',
            serviceType: 'transfer',
            transferCategory: 'Private Car',
        },
        disclaimer: {
            title: 'Child and Baby Seat requirements in Spain',
            description:
                'Please note that in Spain, children under the age of 12, or children less than 135 cm in height are legally required to sit in the back seat in a booster/ child seat. Unless you are bringing your own child / booster seat, we need to ask for you to ensure any required child and booster seats are added to your booking as extras. Our local ride providers, who will be delivering the service, will not be able to provide the transfer you have booked if you do not meet the legal requirements in this regard.',
        },
        providerVoucherUrl: even ? null : 'https://sembo.se',
    };
};

const generateServicePackage = (): ServicePackageProduct | null => {
    const { servicePackageSelection, hasServicePackage } = stored.booking;
    if (!hasServicePackage) return null;

    switch (servicePackageSelection) {
        case ProductType.ServicePackageOld:
        case ProductType.ServicePackageBasic:
        case ProductType.ServicePackagePremium:
            return {
                type: servicePackageSelection,
                name: servicePackageSelection,
                description: 'Call 010-884 80 50 for priority support',
            };
        default:
            return null;
    }
};

const generateTravelInsurance = (count: number): TravelInsuranceProduct[] => {
    return [...Array(count)].map(
        (): TravelInsuranceProduct => ({
            type: ProductType.GenericProduct,
            name: 'Solid Travel Insurance',
            description: 'Solid Travel Insurance',
        })
    );
};

const generateCancellationInsurance = (count: number): CancellationInsuranceProduct[] => {
    return [...Array(count)].map(
        (): CancellationInsuranceProduct => ({
            type: ProductType.CancellationInsurance,
            name: 'Avbeställningsskydd',
            description: 'Avbeställningsskydd',
        })
    );
};

const generateBaggageInsurance = (count: number): BaggageInsuranceProduct[] => {
    return [...Array(count)].map(
        (): BaggageInsuranceProduct => ({
            type: ProductType.BaggageInsurance,
            name: 'Solid bagageförsäkring',
            description:
                '- Solid bagageförsäkring gäller för den försäkrades samtliga bagage.\r\n- Gäller för en resa i en period på högst 30 dagar.\r\n- Ersätter försening av ditt incheckade bagage på en flygresa.\r\n- Erättning utgår redan efter 2 timmar.\r\n- Ersätter stöld och förlust av ditt bagage.',
        })
    );
};

const generateCustomBaggage = (count: number): CustomBaggageProduct[] => {
    if (!stored.products.some((x) => x.type === 'Flight')) {
        return [];
    }

    return [...Array(count)].map((_, index): CustomBaggageProduct => {
        const description = index % 2 === 0 ? '23kg checked baggage' : '1kg hand baggage';

        return {
            type: ProductType.CustomBaggage,
            description,
        };
    });
};

const generateCustomSeating = (count: number): CustomSeatingProduct[] => {
    if (!stored.products.some((x) => x.type === 'Flight')) {
        return [];
    }

    return [...Array(count)].map((): CustomSeatingProduct => {
        return {
            type: ProductType.CustomSeating,
            description: 'Seating',
        };
    });
};

const generateProducts = (): AnyBookingProduct[] => {
    const fakeProducts = stored.products ?? [];

    const products: AnyBookingProduct[] = [];

    fakeProducts.forEach((p, index) => {
        const even = index % 2 === 0;

        switch (p.type) {
            case 'Flight': {
                products.push(generateFlight(index));
                break;
            }
            case 'Ferry': {
                products.push(generateFerry(index));
                break;
            }
            case 'Hotel': {
                products.push(generateHotel(index));
                break;
            }
            case 'Activity': {
                if (even) {
                    products.push(generateGolfActivity());
                } else {
                    products.push(generateActivity(index));
                }
                break;
            }
            case 'Transfer': {
                products.push(generateTransfer(index));
                break;
            }
        }
    });

    const servicePackage = generateServicePackage();

    return [
        ...products,
        ...(servicePackage ? [servicePackage] : []),
        ...generateCancellationInsurance(1),
        ...generateTravelInsurance(1),
        ...generateBaggageInsurance(1),
        ...generateCustomBaggage(2),
        ...generateCustomSeating(1),
    ];
};

const products = generateProducts();

const getMessageProductId = (type: ProductType): string | number | null => {
    const p = products.find((x) => x.type === type);

    if (p) {
        if (p.type === ProductType.Flight) {
            return p.ticket.pnr;
        }

        if (p.type === ProductType.Hotel) {
            return p.rooms[0].productId;
        }
    }

    return null;
};

const getCoupons = (): Coupon[] => {
    if (
        stored.products.some((x) => x.type === 'Flight') &&
        stored.booking.servicePackageSelection
    ) {
        const expirationDate = getFormattedDate(
            convertToDate(
                getDateAddingDuration(new Date(), {
                    months: 6,
                })
            ),
            DateFormat.ISO
        );

        return [
            {
                type: CouponType.Flight,
                code: '1234567-4466-8866',
                amount: { amount: 250, currency },
                termsAmount: { amount: 1000, currency },
                validFor: [ProductType.Hotel],
                expirationDate,
            },
            {
                type: CouponType.Flight,
                code: '1134567-4466-2866',
                amount: { amount: 50, currency },
                termsAmount: { amount: 1000, currency },
                validFor: [ProductType.Activity],
                expirationDate,
            },
        ];
    }

    return [];
};

const getUsedPaymentMethods = (): UsedPaymentMethod[] => {
    if (!stored.booking?.hasLeaBankPayment) {
        return [];
    }

    return [
        {
            type: PaymentMethodType.LeaBank,
            method: null,
            amount: { amount: 11995, currency },
            date: '2024-01-24T12:45:59',
        },
        {
            type: PaymentMethodType.Adyen,
            method: 'visa',
            amount: { amount: 995, currency },
            date: '2024-01-26T12:45:59',
        },
    ];
};

const getCustomerServiceMessages = (): CustomerServiceMessage[] => {
    if (!stored.booking?.hasCustomerServiceMessages) {
        return [];
    }

    return [
        {
            message: 'Vi har försökt nå dig. Ring oss så är du snäll. Tack',
            date: '2023-10-10T14:35:00',
            productId: null,
        },
        {
            message: `<ul><li>Extra-person charges may apply and vary depending on property policy</li><li>Government-issued photo identification and a credit card, debit card, or cash deposit may be required at check-in for incidental charges</li><li>Special requests are subject to availability upon check-in and may incur additional charges; special requests cannot be guaranteed</li></ul> There is no front desk at this property. This property doesn't offer after-hours check-in. `,
            date: null,
            productId: getMessageProductId(ProductType.Flight),
        },
        {
            message: '<Test message> Vi kommer avboka din bokning inom 3 dagar..',
            date: '2023-10-25T10:35:00',
            productId: getMessageProductId(ProductType.Hotel),
        },
        {
            message: 'Lorem ipsum..',
            date: null,
            productId: null,
        },
    ];
};

const getImportantInformation = (): ImportantInformation[] => {
    if (!stored.booking?.hasImportantInformation) {
        return [];
    }

    return [
        {
            type: InformationType.VisaWaiverProgramWarning,
            url: null,
        },
        {
            type: InformationType.ETAProgram,
            url: null,
        },
        {
            type: InformationType.UKVisaWarning,
            url: null,
        },
        {
            type: InformationType.ThailandArrivalCardWarning,
            url: null,
        },
    ];
};

const getCanDisplayPaymentReminder = (): boolean => {
    return Boolean(stored.booking?.canDisplayPaymentReminder);
};

const amountAlreadyPaid = getAmountAlreadyPaid();
const finalPayment = getFinalPayment();
const downPayment = getDownPayment();

export const fakeData: MyBooking = {
    bookingId: getFakeBookingId(),
    bookingNumber: getFakeBookingNumber(),
    password: getFakePassword(),
    created: getCreated(),
    status: getStatus(),
    customStatus: null,
    companyId: 1,
    products: products,
    productCombination: null,
    coupons: getCoupons(),
    customerServiceMessages: getCustomerServiceMessages(),
    importantInformation: getImportantInformation(),
    links: {
        bookingConfirmation: 'https://sembo.se',
        cancellation: 'https://sembo.se',
        giftCard: null,
        termsAndCondition: 'https://www.sembo.se/pages/terms?expediaterms=1',
        sembok: null,
        voucher: {
            url: 'https://sembo.se',
            isMultiTicket: true,
            bookingOnlyHasGiftCertificate: false,
        },
    },
    cancellationPolicies: [
        {
            productId: 270840622,
            fromDate: getDateAddingDuration(new Date(), {
                days: 3,
            }),
            price: 100.0,
            currency: 'EUR',
        },
        {
            productId: 270840622,
            fromDate: getDateAddingDuration(new Date(), {
                days: 10,
            }),
            price: 200.0,
            currency: 'EUR',
        },
        {
            productId: 270840633,
            fromDate: getDateAddingDuration(new Date(), {
                days: 3,
            }),
            price: 150.0,
            currency: 'EUR',
        },
        {
            productId: 270840653,
            fromDate: getDateSubtractingDuration(new Date(), {
                days: 2,
            }),
            price: 0,
            currency: 'EUR',
        },
        {
            productId: 270840653,
            fromDate: getDateAddingDuration(new Date(), {
                days: 0,
            }),
            price: 150.0,
            currency: 'EUR',
        },
        {
            productId: 27084062555,
            fromDate: getDateAddingDuration(new Date(), {
                days: 0,
            }),
            price: 0,
            currency: 'EUR',
        },
        {
            productId: 27084062555,
            fromDate: getDateAddingDuration(new Date(), {
                days: 5,
            }),
            price: 150.0,
            currency: 'EUR',
        },
    ],
    canDisplayPaymentReminder: getCanDisplayPaymentReminder(),

    payment: {
        usedPaymentMethods: getUsedPaymentMethods(),
        downPaymentDueTime: downPayment?.dueTime ?? null,
        paymentDueTime: finalPayment?.dueTime ?? null,
        amountAlreadyPaid: amountAlreadyPaid?.amount ?? null,
        remainingToBePaid: finalPayment?.amount ?? null,
        remainingDownPaymentToBePaid: downPayment?.amount ?? null,
        invoice: null,
        closeToDeparture: true,
        commision: null,
    },
};
